import React, { memo } from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import styles from './Banner.module.scss';
import ESIInclude from '@JohnLewisPartnership/jl-esi-components';
import { useApplicationSettingsContext } from '../../context/ApplicationSettingsContext';
import { useBreadcrumbContext } from '../../context/BreadcrumbContext';

type BrowseProps = {
  isBrandFilterApplied: boolean;
  pathname: string;
};

type SearchProps = {
  searchTerm: string;
  isFilterApplied: boolean;
  pathname: string;
};

type Props = {
  isBrandFilterApplied: boolean;
  isFilterApplied: boolean;
  pathname: string;
};

const CitrusBrowseBanner = memo(
  ({ isBrandFilterApplied, pathname }: BrowseProps) => {
    const { citrusBannerService } = useApplicationSettingsContext();
    const { breadcrumbs } = useBreadcrumbContext();

    if (!Array.isArray(breadcrumbs) || !breadcrumbs?.length) return null;

    const formattedBreadcrumbs = encodeURIComponent(
      breadcrumbs.map((b) => b.displayName).join('|')
    );

    const brandFilterAppliedString = isBrandFilterApplied
      ? '&brandFilterApplied=true'
      : '';

    // pathname is an arbitrary string used to trigger new ad impressions in the ESI
    const pathnameString = pathname ? `&pathname=${pathname}` : '';

    return (
      <aside
        data-test="banner-container"
        className={classNames(styles.container, styles.citrusbanners)}
      >
        <ESIInclude
          src={`${citrusBannerService}/browse?browsePageHierarchy=${formattedBreadcrumbs}${brandFilterAppliedString}${pathnameString}`}
          id="esiCitrusBanner"
        />
      </aside>
    );
  }
);
CitrusBrowseBanner.displayName = 'CitrusBrowseBanner';

const CitrusSearchBanner = memo(
  ({ searchTerm, isFilterApplied, pathname }: SearchProps) => {
    const { citrusBannerService } = useApplicationSettingsContext();

    if (!searchTerm) return null;

    const isFilterAppliedString = isFilterApplied ? '&filterApplied=true' : '';

    // pathname is an arbitrary string used to trigger new ad impressions in the ESI
    const pathnameString = pathname ? `&pathname=${pathname}` : '';

    return (
      <aside
        data-test="banner-container"
        className={classNames(styles.container, styles.citrusbanners)}
      >
        <ESIInclude
          src={`${citrusBannerService}/search?searchTerm=${encodeURIComponent(
            searchTerm
          )}${isFilterAppliedString}${pathnameString}`}
          id="esiCitrusBanner"
        />
      </aside>
    );
  }
);
CitrusSearchBanner.displayName = 'CitrusSearchBanner';

export const CitrusBanner = memo(
  ({ isBrandFilterApplied, isFilterApplied, pathname }: Props) => {
    const router = useRouter();
    const { type, 'search-term': searchTerm } = router.query;

    if (type === 'browse') {
      return (
        <CitrusBrowseBanner
          isBrandFilterApplied={isBrandFilterApplied}
          pathname={pathname}
        />
      );
    }

    if ((type === 's' || type === 'search') && typeof searchTerm == 'string') {
      return (
        <CitrusSearchBanner
          searchTerm={searchTerm}
          isFilterApplied={isFilterApplied}
          pathname={pathname}
        />
      );
    }

    return null;
  }
);
CitrusBanner.displayName = 'CitrusBanner';
